import {TrainService} from "@oebb-web-team/oebb-web-components";
import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State, Watch } from '@stencil/core';

import {asOffsetTime, atStartOfDay, getLaterDate, toDateSameLocalTime} from '../../utils/date';
import { i18n } from '../../utils/I18n';
import { serverAddress } from '../../helpers/utils';
import {
	DATA_STORE_KEY_FROM_TO,
	DATA_STORE_KEY_FROM_TO_RETURN,
	DATA_STORE_KEY_SESSION_CORRUPTED,
	dataStore,
	SessionHealthState
} from '../../global/DataStore';
import {passengersAreValid} from "../nj-passangers/passanger-utils";
import {trackMatomoEvent} from "../../utils/matomo";

export interface FromToData {
  from: { value: string; context?: any };
  to: { value: string; context?: any };
  dateOutward?: { value: string; context: Date };
  dateReturn?: { value: string; context: Date };
  oneWay?: boolean;
  mode: FromToMode;
  manualSubmit?: boolean;
}

export type FromToMode = 'default' | 'outward' | 'return';

@Component({
  tag: 'nj-from-to',
  styleUrls: {
    default: 'nj-from-to.scss',
    outward: 'nj-from-to.scss',
    return: 'nj-from-to.scss',
  },
  shadow: true,
})
export class NjFromTo {
  private data: FromToData;

  private readonly today: Date = toDateSameLocalTime(atStartOfDay());

  private initialOutwardDate: Date;
  private initialReturnDate: Date;

  @Element() el: HTMLElement;
  @State() formIsCompleted: boolean = null;
  @State() formDataValidationEnabled: boolean = false;
  @State() oneWay: boolean = false;
  @State() returnDateDisabledBefore: Date;
  @Prop() mode: FromToMode;
  @Prop() submitLabel: string = i18n.getMessage('fromTo.searchConnections', 'Verbindungen suchen');

  @Watch('mode')
  modeChanged(newValue: FromToMode, oldValue: FromToMode) {
    if (newValue == 'default' && oldValue == 'outward') {
      // Search for one-way connection after switching from outward mode to default mode
      this.oneWay = true;
      if (this.data) {
        this.data.oneWay = true;
      }
    }
  }

  @Prop() returnDateAddDays: number = 1;

  @Event() njFromToFormSubmitted: EventEmitter<FromToData>;
  @Event() njFromToFormNotComplited: EventEmitter<FromToData>;
  @Prop() showFormOnly: boolean;

  @State()
  private evaFrom: string;

  @State()
  // @ts-ignore
  private evaTo: string;

  @State()
  private userCountry: string;

  private fromFieldIsValid = false;
  private toFieldIsValid = false;
  private passengersValid = false;

  componentWillLoad() {
	  this.loadUserCountry().then();

      const outwardData: FromToData = dataStore().getSessionData(DATA_STORE_KEY_FROM_TO);

      if (this.mode === 'return') {
          const returnData: FromToData = dataStore().getSessionData(DATA_STORE_KEY_FROM_TO_RETURN);
          if (returnData) {
              // Return data already present, use it
              this.data = returnData;
          } else if (outwardData) {
              // Invert the outward data
              this.data = {
                  oneWay: false,
                  from: {...outwardData.to},
                  to: {...outwardData.from},
                  dateReturn: {...outwardData.dateReturn},
                  mode: 'return',
              };
          }
      } else {
          this.data = outwardData;
      }

      // Enable the return field one day after the requested outward date, or tomorrow if no outward date exists
      this.returnDateDisabledBefore = asOffsetTime(outwardData?.dateOutward?.context ?? this.today).add(1, 'day').toDate();

      // Set initial outward/return dates
      this.initialOutwardDate = getLaterDate(this.data?.dateOutward?.context, this.today);
      this.initialReturnDate = getLaterDate(this.data?.dateReturn?.context, this.returnDateDisabledBefore);

      if (this.data) {
          // Set the initial station numbers
          this.evaFrom = this.data.from?.context.number;
          this.evaTo = this.data.to?.context.number;
      }
  }

  async componentDidLoad() {
	  // trackEvent(['enableDebugMode']); // TODO: remove after debugging

    this.updateFromClassesFromMode();

    if (this.data) {
        // Set the "to" field first so the autofocus after setting the "from" field behaves correctly
		const promises = [];
		if (this.data.to?.context) {
			promises.push(this.el.shadowRoot.querySelector<HTMLOebbxStationFieldElement>('.js-station-to').setValue(this.data.to.context));
		}
		if (this.data.from?.context) {
			promises.push(await this.el.shadowRoot.querySelector<HTMLOebbxStationFieldElement>('.js-station-from').setValue(this.data.from.context));
		}
		if (promises.length) {
			await Promise.all(promises);
		}

        this.oneWay = this.data.oneWay;
        this.formDataValidationEnabled = true;
        await this.validate();
    }
  }

  componentDidUpdate() {
    this.updateFromClassesFromMode();
  }

  private async loadUserCountry() {
	  try {
		  this.userCountry = await TrainService.instance.getGeolocation();
	  } catch (e) {
		  console.error("Error loading user country");
		  this.userCountry = null;
	  }
  }

  @Listen('formDataChanged')
  async formDataChangedHandler(event: CustomEvent) {
   // 	console.log("formDataChanged",event.detail)
      const eventData = event.detail

     // console.log("firstChange",eventData.firstChange)

      switch (eventData.name) {
          case "from":
              this.evaFrom = eventData.valid ? eventData.value?.number : null;
              if (eventData.valid) {
                  const toField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-to');
                  if (toField && !await toField.isValid()) {
                      await toField.setFocus();
                  }
              }
              break;
          case "to":
              this.evaTo = eventData.valid ? eventData.value?.number : null;
              break;
      }

      await this.validate();
      if (this.formIsCompleted) {
          await this.handleSubmit();
      }
  }

  updateFromClassesFromMode() {
    const form: HTMLFormElement = this.el.shadowRoot.querySelector('form');

    form.classList.forEach(function (className: string) {
      if (className.includes('nj-from-to--')) form.classList.remove(className);
    });
    form.classList.add('nj-from-to--' + this.mode);
  }

  @Listen('formDataChanged', {target: 'window'})
  async outwardDateChangedHandler(event: CustomEvent) {
    if (event.detail.name !== 'outwardDate' || !event.detail.value) {
      return;
    }

    // Update the allowed return date range
    this.returnDateDisabledBefore = asOffsetTime(event.detail.value).add(1, 'day').toDate();

    const returnField: HTMLOebbxDateFieldElement = this.el.shadowRoot.querySelector('.js-date-return');
    if (!returnField) {
      // Return field not visible: Just update the initial date for when it becomes visible again
      this.initialReturnDate = this.returnDateDisabledBefore;
      return;
    }

    const returnDate: Date = await returnField.getDate();
    if (returnDate < this.returnDateDisabledBefore) {
      // Return date is out of valid range, move it up
      this.initialReturnDate = this.returnDateDisabledBefore;
      await returnField.setDate(this.returnDateDisabledBefore);
    }

    await this.validate();
  }

  async handleReturnCheckboxChange(e: Event) {
    this.oneWay = (e.target as HTMLInputElement).checked;
    setTimeout(async () => {
      // Wait for the return field to be updated, then reload data
      if (this.formIsCompleted) {
        await this.handleSubmit(null);
      }
    }, 300);
	trackMatomoEvent({ "event": "nj.home.searchconnection.outward.only", "nj.click": this.oneWay });
  }

  async handleUnfinishedForm() {
    this.formIsCompleted = false;

    const fromField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-from');
    const toField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-to');

    fromField.validateErrorMessage();
    toField.validateErrorMessage();
  }

  async validate() {
    if (this.formDataValidationEnabled) {
      const fromField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-from');
      const toField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-to');
      const outwardDateField: HTMLOebbxDateFieldElement = this.el.shadowRoot.querySelector('.js-date-outward');
      const returnDateField: HTMLOebbxDateFieldElement = this.el.shadowRoot.querySelector('.js-date-return');

      let returnDateFieldIsValid: boolean = true;
      if(returnDateField){
      //	console.log(await returnDateField.getDate())
        returnDateFieldIsValid=(await returnDateField.isValid())
      }

      let outwardDateFieldIsValid: boolean = true;
      if(outwardDateField){
        //console.log(await outwardDateField.getDate())
        outwardDateFieldIsValid=(await outwardDateField.isValid())
      }
      this.fromFieldIsValid = await fromField.isValid();
      this.toFieldIsValid = (!toField || await toField.isValid());

      this.passengersValid = passengersAreValid();

      this.formIsCompleted = this.fromFieldIsValid && this.toFieldIsValid && returnDateFieldIsValid && outwardDateFieldIsValid && this.passengersValid;
    }
  }

  @Method()
  async submitForm() {
    await this.handleSubmit(null, true);
  }

    async handleSubmit(e?: Event, manualSubmit?: boolean) {
        if (e) { e.preventDefault(); }
        this.formDataValidationEnabled = true;

        const fromField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-from');
        const toField: HTMLOebbxStationFieldElement = this.el.shadowRoot.querySelector('.js-station-to');
        const passangersField: HTMLNjPassangerFieldElement = this.el.shadowRoot.querySelector('.js-passangers-outward');

        await this.validate();
        if (this.formIsCompleted) {
            const fromValue: string = await fromField.getValue();
            const fromItem: any = await fromField.getItem();
            const toValue: string = await toField.getValue();
            const toItem: any = await toField.getItem();
            let dateOutwardValue: string;
            let dateOutwardDate: Date;

            const data: FromToData = {
                oneWay: this.oneWay,
                from: {
                    value: fromValue,
                    context: fromItem,
                },
                to: {
                    value: toValue,
                    context: toItem,
                },
                mode: this.mode,
                manualSubmit,
            };

            const dateOutward = this.el.shadowRoot.querySelector('.js-date-outward') as HTMLOebbxDateFieldElement;
            if (this.hasOutwardDate() && dateOutward) {
                dateOutwardValue = await dateOutward.getValue();
                dateOutwardDate = await dateOutward.getDate();

                data['dateOutward'] = {
                    value: dateOutwardValue,
                    context: dateOutwardDate,
                };
            }

            const dateReturn = this.el.shadowRoot.querySelector('.js-date-return') as HTMLOebbxDateFieldElement;
            if (this.hasReturnDate() && dateReturn) {
                const dateReturnValue: string = await dateReturn.getValue();
                const dateReturnDate: Date = await dateReturn.getDate();

                data['dateReturn'] = {
                    value: dateReturnValue,
                    context: dateReturnDate,
                };
            }

            if (!this.isNewData(data)) {
                // Only trigger event if data really changed
                return;
            }

            this.data = data;
            //  console.log("from-to",this.mode,this.data)
            if ((this.mode == 'outward' || this.mode == 'default') && fromValue.length > 0 && toValue.length > 0) {
                this.formIsCompleted = true;
                trackMatomoEvent({ "event": "nj.home.searchconnection.searchbutton.clicked", "nj.click": Date.now() });
                this.njFromToFormSubmitted.emit(this.data);
            }
            else if (this.mode == 'return' && fromValue.length > 0 && toValue.length > 0) {
                this.formIsCompleted = true;
                trackMatomoEvent({ "event": "nj.home.searchconnection.searchbutton.clicked", "nj.click": Date.now() });
                this.njFromToFormSubmitted.emit(this.data);
            }
            else {
                this.handleUnfinishedForm();
                this.njFromToFormNotComplited.emit(this.data);
            }
        } else {
            this.handleUnfinishedForm();
            this.njFromToFormNotComplited.emit(this.data);

            if (!this.fromFieldIsValid) {
                fromField.setFocus();
            } else if (!this.toFieldIsValid) {
                toField.setFocus();
            } else if (!this.passengersValid) {
                passangersField?.setFocus();
            }
        }
    }

  private hasOutwardDate(): boolean {
      return this.mode === 'default' || this.mode === 'outward';
  }

  private hasReturnDate(): boolean {
      return this.mode === 'return' || (this.mode === 'default' && !this.oneWay);
  }

  private isNewData(data: FromToData): boolean {
      if (!this.data || data.manualSubmit) {
          // First or manual trigger: run in any case
          return true;
      }

      if (data.oneWay !== this.data.oneWay) {
          return true;
      }
      if (data.from.context?.number !== this.data.from.context?.number) {
          return true;
      }
      if (data.to.context?.number !== this.data.to.context?.number) {
          return true;
      }
      if (this.hasOutwardDate() && data.dateOutward?.context?.getTime() !== this.data.dateOutward?.context?.getTime()) {
          return true;
      }
      if (this.hasReturnDate() && data.dateReturn?.context?.getTime() !== this.data.dateReturn?.context?.getTime()) {
          return true;
      }

      // Nothing changed, do not trigger reload
      return false;
  }

  private renderReturnDateLabel(): any {
      return [
          i18n.getMessage('fromTo.return', 'Rückfahrt'),
          ' ',
          this.mode !== "return" ? (
              <nj-infobox onClick={this.trackReturnJourneyInfoIconClick} waAriaLabel={i18n.getMessage('fromTo.return', 'Rückfahrt')}>
                  <p>{i18n.getMessage("additional.selected.return.hoverinformation")}</p>
              </nj-infobox>
          ) : null
      ]
  }

  private readonly trackReturnJourneyInfoIconClick = () => {
	  trackMatomoEvent({ "event": "nj.home.returnInfoClicked" });
  };

  handleStationSelected(event: CustomEvent, mode: "from" | "to") {
	  trackMatomoEvent({ "event": `nj.home.searchconnection.${mode}.selected`, "nj.selection": event.detail.name?.trim(), "nj.click": event.detail.name });
  }

  render() {
    return (
      <form id="nj-from-to" method="GET" class="nj-from-to" onSubmit={(e: Event) => this.handleSubmit(e, true)}>
        <div class="from-to">
          <p>
            <oebbx-station-field name="from" label={i18n.getMessage('fromTo.from', 'Von')}
                                 url={`${serverAddress()}/stations/find?lang=${i18n.getLang()}&country=${this.userCountry || ''}&name=`}
                                 class="js-station-from"
								 showMetaSuffix={true}
								 onStationSelected={(station) => this.handleStationSelected(station, "from")}
                 errorMessageId="fromToFromErrorMessage"
                 missingStationMessage={i18n.getMessage('fromTo.from.valueMissing')} />
          </p>
          <p>
            <oebbx-station-field name="to" label={i18n.getMessage('fromTo.to', 'Nach')}
                                 url={`${serverAddress()}/stations/find?lang=${i18n.getLang()}&evaFrom=${this.evaFrom ?? ''}&name=`}
                                 class="js-station-to"
								 showMetaSuffix={true}
								 onStationSelected={(station) => this.handleStationSelected(station, "to")}
                 errorMessageId="fromToToErrorMessage"
                 missingStationMessage={i18n.getMessage('fromTo.to.valueMissing')} />

          </p>
        </div>

        <div class="date-passengers">
          <div>
            <div>
              {this.hasOutwardDate() ? (
                <p>
                  <oebbx-date-field
                    name="outwardDate"
                    mode="2021"
                    startDate={this.initialOutwardDate}
                    disabledBefore={this.today}
                    disabledAfter={TrainService.instance.getMaxBookableDate()}
                    label={i18n.getMessage('fromTo.outward', 'Hinfahrt')}
                    class="js-date-outward"
                  />
                </p>
              ) : null}

              {this.hasReturnDate() ? (
                <p class="returnDateContainer">
                  <oebbx-date-field
                    name="returnDate"
                    mode="2021"
                    startDate={this.initialReturnDate}
                    disabledBefore={this.returnDateDisabledBefore}
                    disabledAfter={TrainService.instance.getMaxBookableDate()}
                    label={this.renderReturnDateLabel()}
                    class="js-date-return"
                  />
                </p>
              ) : (
                ''
              )}
            </div>

           {this.mode === 'default' ? (
              <p>
                <input type="checkbox" name="nj-from-to-return" id="nj-from-to-return" checked={this.oneWay} onChange={(e: Event) => this.handleReturnCheckboxChange(e)} />
                <label htmlFor="nj-from-to-return">{i18n.getMessage('fromTo.onlyOutward', 'nur Hinfahrt')}</label>
              </p>
           ) : null
        }
          </div>

          {this.hasOutwardDate() ? (
            <p>
              <nj-passanger-field class="js-passangers-outward" show-validation={this.formIsCompleted === false}/>
            </p>
          ) : null}
          {/* 	{this.mode == 'default' || this.mode == 'outward' ? <p><nj-motorail-field class="js-motorail-outward" /></p> : null}  */}
        </div>

        <div class="actions">
          {this.formIsCompleted == false ? <p class="nj-from-to-error-message" role="alert" aria-live="assertive">{i18n.getMessage('fromTo.error.valueMissing', 'Bitte füllen Sie die fehlenden Felder aus.')}</p> : ''}

          <input type="submit"
            disabled={dataStore().getSessionData(DATA_STORE_KEY_SESSION_CORRUPTED) === SessionHealthState.CORRUPTED}
            value={this.submitLabel}
            class="submitButton"
          />
        </div>
      </form>
    );
  }
}
